<template>
  
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15">
      <!---->
      <h2 class="fs-4 text-black fw-semibold mb-10 text-center">Reprendre la session</h2>
      
      <div v-if="isLoading" class="text-center">
        <p class="mb-10">Chargement des questions...</p>
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
      </div>

      <div v-else-if="showResults" class="results-container">
        <div>
          <div class="text-center"><h3 class="mb-20">Test Terminé !</h3></div>
          <div class="row gx-2">
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Score</h4>
                <p class="fs-1 fw-bold text-success">{{ testResults.score }} / {{ testResults.total }}</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Pourcentage</h4>
                <p class="fs-1 fw-bold" :class="testResults.percentage >= 50 ? 'text-success' : 'text-danger'">
                  {{ testResults.percentage }}%
                </p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Bonnes réponses</h4>
                <p class="fs-1 fw-bold text-success">{{ testResults.correctAnswers }}</p>
              </div>
            </div>
            <div class="col-md-3">
              <div class="result-card p-15 mb-5 bg-success-subtle">
                <h4 class="text-primary">Mauvaises réponses</h4>
                <p class="fs-1 fw-bold text-danger">{{ testResults.incorrectAnswers }}</p>
              </div>
            </div>
          </div>
          
          <div class="text-start mt-10">
            <h4 class="mb-15 text-center">📋 Revue détaillée de vos réponses</h4>
            <div class="questions-review">
              <div 
                v-for="(question, index) in questionsWithAnswers" 
                :key="question.id"
                class="question-review-item mb-15 border"
                :class="question.isCorrect ? 'border-success' : 'border-danger'"
              >
                <div class="question-header p-5" :class="question.isCorrect ? 'bg-success-subtle' : 'bg-danger-subtle'">
                  <div class="d-flex align-items-center justify-content-between">
                    <h5 class="mb-0 fw-bold">
                      <span class="question-number">Question {{ index + 1 }}</span>
                    </h5>
                    <span class="fs-3" :class="question.isCorrect ? 'text-success' : 'text-danger'">
                      {{ question.isCorrect ? '✓' : '✗' }}
                    </span>
                  </div>
                </div>
                
                <div class="question-content p-10 bg-white">
                  <p class="fw-semibold mb-10 text-black">{{ question.contenu }}</p>
                  
                  <div class="user-answer mb-5 p-10 rounded" :class="question.isCorrect ? 'bg-light' : 'bg-light'">
                    <strong class="text-black">Votre réponse :</strong>
                    <span 
                      class="ms-2 fw-semibold"
                      :class="question.isCorrect ? 'text-success' : 'text-danger text-decoration-line-through'"
                    >
                      {{ question.userAnswerText }}
                    </span>
                  </div>
                  
                  <div v-if="!question.isCorrect" class="correct-answer-display p-10 bg-success-subtle rounded">
                    <strong class="text-success">✓ Bonne réponse :</strong>
                    <span class="ms-2 text-success fw-bold">
                      {{ question.correctAnswerText }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="completeHistory.length > 0" class="text-start mt-20">
            <h4 class="mb-10">📜 Historique des scores </h4>
            <div class="alert alert-info mb-10">
              <strong>Note :</strong> Cet historique inclut le score de la session originale et tous les scores des reprises effectuées.
            </div>
            <div class="table-responsive">
              <table class="table text-nowrap align-middle mb-0 table-bordered">
                <thead class="table-light">
                  <tr>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Type
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Tentative
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Score
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Total
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Pourcentage
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Bonnes
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Mauvaises
                    </th>
                    <th scope="col" class="text-uppercase fw-medium shadow-none text-body-tertiary fs-13 pt-2 pb-2">
                      Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(result, index) in completeHistory" :key="`history-${index}`" :class="result.isOriginal ? 'table-primary' : ''">
                    <td class="shadow-none lh-1 fw-medium">
                      <span v-if="result.isOriginal" class="badge bg-primary">
                        <i class="fa fa-star me-1"></i>Originale
                      </span>
                      <span v-else class="badge bg-secondary">
                        <i class="fa fa-refresh me-1"></i>Reprise
                      </span>
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                      {{ result.attempt }}
                    </td>
                    <td class="shadow-none lh-1 fw-bold text-black-emphasis">
                      {{ result.score }}
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                      {{ result.total }}
                    </td>
                    <td class="shadow-none lh-1 fw-bold">
                      <span :class="result.percentage >= 50 ? 'text-success' : 'text-danger'">
                        {{ result.percentage }}%
                      </span>
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-success">
                      {{ result.correctAnswers }}
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-danger">
                      {{ result.incorrectAnswers }}
                    </td>
                    <td class="shadow-none lh-1 fw-medium text-black-emphasis">
                      {{ result.updatedAt ? format_date(result.updatedAt) : 'N/A' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <!-- Statistiques de progression -->
            <div class="row mt-15 gx-2" v-if="completeHistory.length > 1">
              <div class="col-md-12">
                <div class="alert alert-success">
                  <h5 class="mb-10"><i class="fa fa-chart-line me-2"></i>Analyse de progression</h5>
                  <div class="row">
                    <div class="col-md-4">
                      <strong>Meilleur score :</strong>
                      <span class="ms-2 text-success fw-bold">{{ bestScore.score }}/{{ bestScore.total }} ({{ bestScore.percentage }}%)</span>
                    </div>
                    <div class="col-md-4">
                      <strong>Score moyen :</strong>
                      <span class="ms-2 text-primary fw-bold">{{ averagePercentage }}%</span>
                    </div>
                    <div class="col-md-4">
                      <strong>Nombre de tentatives :</strong>
                      <span class="ms-2 text-info fw-bold">{{ completeHistory.length }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-center gap-3 mt-20">
            <router-link to="/FaireTest" class="btn btn-primary">
              <i class="fa fa-refresh me-1"></i>
              Nouveau Test
            </router-link>
            <router-link to="/liste-questions" class="btn btn-info">
              <i class="fa fa-list me-1"></i>
              Voir les Questions
            </router-link>
            <router-link to="/liste-sessions" class="btn btn-secondary">
              <i class="fa fa-history me-1"></i>
              Historique
            </router-link>
          </div>
        </div>
      </div>

      <Form
        v-else-if="questions.length > 0"
        ref="testForm"
        @submit="handleSubmit"
        :validation-schema="testSchema"
      >
        <div class="progress mb-20" style="height: 8px;">
          <div 
            class="progress-bar bg-success" 
            :style="{ width: progressPercentage + '%' }"
          ></div>
        </div>

        <div v-if="currentQuestion" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Question {{ currentQuestionIndex + 1 }} / {{ questions.length }}
          </label>
          <div class="alert alert-info">
            <strong>{{ currentQuestion.contenu }}</strong>
          </div>
        </div>

        <div
          v-if="currentQuestion && currentQuestion.type === 'choix_multiple'"
          class="form-group mb-10"
        >
          <label class="d-block text-black fw-semibold mb-10">
            Sélectionner une réponse <span class="text-danger">*</span>
          </label>
          <div v-if="propositionOptions.length > 0" class="border border-primary p-10">
            <div v-for="(proposition, index) in propositionOptions" :key="`prop-${proposition.value}-${index}`">
              <div class="d-flex align-items-center mb-5">
                <Field
                  name="answer"
                  type="radio"
                  :value="proposition.value"
                  v-model="formData.answer"
                  :id="`proposition-${proposition.value}`"
                  class="form-check-input me-2"
                  @change="onAnswerChange"
                />
                <label
                  class="text-black"
                  :for="`proposition-${proposition.value}`"
                  style="cursor: pointer;"
                >
                  {{ proposition.label }}
                </label>
              </div>
            </div>
            <ErrorMessage name="answer" class="text-danger" />
          </div>
          <div v-else class="text-danger">
            Aucune proposition disponible pour cette question.
          </div>
        </div>

        <div v-else-if="currentQuestion && currentQuestion.type === 'texte_libre'" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Votre réponse <span class="text-danger">*</span>
          </label>
          <Field
            name="answer"
            as="textarea"
            class="form-control shadow-none text-black"
            v-model="formData.answer"
            placeholder="Entrez votre réponse"
            rows="4"
            @input="onAnswerChange"
          />
          <ErrorMessage name="answer" class="text-danger" />
        </div>

        <div v-else-if="currentQuestion && currentQuestion.type === 'oui_non'" class="form-group mb-10">
          <label class="d-block text-black fw-semibold mb-10">
            Sélectionner une réponse <span class="text-danger">*</span>
          </label>
          <div class="border border-primary p-10">
            <div class="d-flex align-items-center mb-5">
              <Field
                name="answer"
                type="radio"
                value="Oui"
                v-model="formData.answer"
                id="oui"
                class="form-check-input me-2"
                @change="onAnswerChange"
              />
              <label class="text-black" for="oui" style="cursor: pointer;">Oui</label>
            </div>
            <div class="d-flex align-items-center mb-5">
              <Field
                name="answer"
                type="radio"
                value="Non"
                v-model="formData.answer"
                id="non"
                class="form-check-input me-2"
                @change="onAnswerChange"
              />
              <label class="text-black" for="non" style="cursor: pointer;">Non</label>
            </div>
            <ErrorMessage name="answer" class="text-danger" />
          </div>
        </div>

               <!-- Actions (boutons) -->
        <div class="d-flex align-items-center mt-10">
          <button
            type="button"
            class="btn btn-secondary me-3"
            @click="previousQuestion"
            :disabled="currentQuestionIndex === 0"
            :class="{ 'cursor-not-allowed': currentQuestionIndex === 0 }"
          >
            Précédent
          </button>

          <button
            type="submit"
            class="btn"
            :class="[
              isLastQuestion ? 'btn-success' : 'btn-info',
              { 'cursor-not-allowed': isDisable }
            ]"
            :disabled="isDisable || isSubmitting"
          >
            <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else :class="isLastQuestion ? 'fa fa-check me-1' : 'fa fa-arrow-right me-1'"></i>
            {{ isLastQuestion ? 'Terminer le test' : 'Suivant' }}
          </button>
        </div>
        <div class="mt-10" v-if="isDisable">
          <small class="text-danger">
            
            <span v-if="!currentQuestion">Aucune question disponible</span>
            
          </small>
        </div>
        <div class="mt-10" v-else>
          <small class="text-success">
            <i class="fa fa-check-circle me-1"></i>
            Prêt à continuer
          </small>
        </div>
      </Form>

      <div v-else class="text-danger text-center">
        <div class="alert alert-danger">
          <h4>Test impossible à reprendre</h4>
          <p class="mb-10">
            Aucune question n'est associée à cette session.
          </p>
          <div class="d-flex justify-content-center gap-2">
            <router-link to="/liste-sessions" class="btn btn-primary">
              <i class="fa fa-arrow-left me-1"></i>
              Retour
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted, watch } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import * as Yup from 'yup';
import { useRouter, useRoute } from 'vue-router';
import { success, error, format_date } from '@/utils/utils';
import ApiService from '@/services/ApiService';

interface Proposition {
  id: number;
  contenu: string;
  estLaBonneReponse?: boolean;
}

interface Question {
  id: number;
  contenu: string;
  type: string;
  propositions?: Proposition[];
}

interface FormValues {
  answer: string | null;
}

interface TestResults {
  score: number;
  total: number;
  percentage: number;
  correctAnswers: number;
  incorrectAnswers: number;
}

interface QuestionWithAnswer {
  id: number;
  contenu: string;
  type: string;
  userAnswerText: string;
  correctAnswerText: string;
  isCorrect: boolean;
}

interface ResultHistory {
  attempt: number;
  score: number;
  total: number;
  percentage: number;
  correctAnswers: number;
  incorrectAnswers: number;
  updatedAt: Date | null;
  isOriginal?: boolean;
}

export default defineComponent({
  name: 'ReprendreSessionPage',
  components: {
    Form,
    Field,
    ErrorMessage,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const testForm = ref<InstanceType<typeof Form> | null>(null);
    const questions = ref<Question[]>([]);
    const currentQuestionIndex = ref(0);
    const isLoading = ref(true);
    const isSubmitting = ref(false);
    const sessionId = ref<number | null>(null);
    const showResults = ref(false);
    const testResults = reactive<TestResults>({
      score: 0,
      total: 0,
      percentage: 0,
      correctAnswers: 0,
      incorrectAnswers: 0,
    });
    const resultHistory = ref<ResultHistory[]>([]);
    const formData = reactive<FormValues>({
      answer: null,
    });
    const answers = ref<{ [key: number]: string }>({});

    const testSchema = Yup.object().shape({
      answer: Yup.string().required('Une réponse est obligatoire'),
    });

    const currentQuestion = computed(() => {
      return questions.value[currentQuestionIndex.value] || null;
    });

    const propositionOptions = computed(() => {
      if (currentQuestion.value && currentQuestion.value.type === 'choix_multiple') {
        console.log('Propositions brutes:', currentQuestion.value.propositions);
        const options = currentQuestion.value.propositions?.map((prop) => ({
          value: String(prop.id),
          label: prop.contenu || 'Proposition inconnue',
        })) || [];
        console.log('PropositionOptions:', options);
        return options;
      }
      return [];
    });

    const questionsWithAnswers = computed((): QuestionWithAnswer[] => {
      return questions.value.map((question) => {
        const userAnswer = answers.value[question.id];
        let userAnswerText = 'Non répondu';
        let correctAnswerText = 'Non disponible';
        let isCorrect = false;

        const correctProposition = question.propositions?.find(p => p.estLaBonneReponse);
        if (correctProposition) {
          correctAnswerText = correctProposition.contenu;
        }

        if (userAnswer) {
          if (question.type === 'choix_multiple' || question.type === 'oui_non') {
            const userProposition = question.propositions?.find(p => String(p.id) === userAnswer);
            userAnswerText = userProposition ? userProposition.contenu : userAnswer;
            isCorrect = userProposition?.estLaBonneReponse || false;
          } else if (question.type === 'texte_libre') {
            userAnswerText = userAnswer;
            isCorrect = correctProposition ? userAnswer.toLowerCase().trim() === correctProposition.contenu.toLowerCase().trim() : false;
          }
        }

        return {
          id: question.id,
          contenu: question.contenu,
          type: question.type,
          userAnswerText,
          correctAnswerText,
          isCorrect,
        };
      });
    });

    const completeHistory = computed((): ResultHistory[] => {
      return resultHistory.value.map((result) => ({
        ...result,
        isOriginal: result.attempt === 0,
      })).sort((a, b) => a.attempt - b.attempt);
    });

    const bestScore = computed(() => {
      if (completeHistory.value.length === 0) {
        return { score: 0, total: 0, percentage: 0 };
      }
      return completeHistory.value.reduce((best, current) => {
        return current.percentage > best.percentage ? current : best;
      });
    });

    const averagePercentage = computed(() => {
      if (completeHistory.value.length === 0) return 0;
      const sum = completeHistory.value.reduce((acc, curr) => acc + curr.percentage, 0);
      return Math.round(sum / completeHistory.value.length);
    });

    const isLastQuestion = computed(() => {
      return currentQuestionIndex.value === questions.value.length - 1;
    });

    const isDisable = computed(() => {
      return !currentQuestion.value || !formData.answer || formData.answer.trim() === '';
    });

    const progressPercentage = computed(() => {
      if (questions.value.length === 0) return 0;
      return Math.round(((currentQuestionIndex.value + 1) / questions.value.length) * 100);
    });

    const answeredCount = computed(() => {
      return Object.keys(answers.value).length;
    });

    const resetState = () => {
      questions.value = [];
      answers.value = {};
      currentQuestionIndex.value = 0;
      formData.answer = null;
      showResults.value = false;
      Object.assign(testResults, {
        score: 0,
        total: 0,
        percentage: 0,
        correctAnswers: 0,
        incorrectAnswers: 0,
      });
      resultHistory.value = [];
    };

    const loadSessionQuestions = async () => {
      try {
        isLoading.value = true;
        resetState();
        
        sessionId.value = Number(route.params.id);
        console.log('Chargement des questions pour la session:', sessionId.value);
        
        const { data } = await ApiService.get(`/sessions/${sessionId.value}/questions`);
        if (data.code === 200 && data.data) {
          questions.value = data.data;
          console.log('Questions chargées:', questions.value.length);
          
          currentQuestionIndex.value = 0;
          const firstQuestion = questions.value[0];
          formData.answer = answers.value[firstQuestion?.id || 0] || null;
          
          if (testForm.value) {
            testForm.value.resetForm({ values: { answer: formData.answer } });
          }
          onAnswerChange();

          await fetchResultHistory();
        } else {
          throw new Error('Aucune question trouvée pour cette session');
        }
      } catch (err: any) {
        console.error('Erreur lors du chargement des questions:', err);
        error(err.response?.data?.message || 'Erreur lors du chargement des questions');
      } finally {
        isLoading.value = false;
      }
    };

    const fetchTestResults = async () => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible de récupérer les résultats');
        return;
      }

      try {
        console.log('Récupération des résultats pour la session:', sessionId.value);
        const { data } = await ApiService.get(`/sessions/${sessionId.value}/results`);
        
        console.log('Résultats bruts reçus:', data);
        
        if (data && data.code === 200 && data.data) {
          Object.assign(testResults, {
            score: data.data.score || 0,
            total: data.data.total || questions.value.length,
            percentage: data.data.percentage || 0,
            correctAnswers: data.data.correctAnswers || 0,
            incorrectAnswers: data.data.incorrectAnswers || 0,
          });
          
          console.log('Résultats mis à jour:', testResults);
        } else {
          throw new Error('Format de réponse invalide pour les résultats');
        }
      } catch (err: any) {
        console.error('Erreur lors de la récupération des résultats:', err);
        error(`Impossible de récupérer les résultats: ${err.message || 'Erreur inconnue'}`);
      }
    };

    const fetchResultHistory = async () => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible de récupérer l\'historique');
        return;
      }
      try {
        console.log('Récupération de l\'historique pour la session:', sessionId.value);
        const { data } = await ApiService.get(`/sessions/${sessionId.value}/history`);
        if (data.code === 200 && data.data) {
          console.log('Données brutes de l\'historique:', data.data);
          
          resultHistory.value = data.data
            .map((item: any) => {
              const updatedAt = item.updatedAt ? new Date(item.updatedAt) : null;
              if (updatedAt && isNaN(updatedAt.getTime())) {
                console.warn(`Date invalide pour updatedAt: ${item.updatedAt}`);
              }
              return {
                attempt: item.attempt,
                score: item.score,
                total: item.total,
                percentage: item.percentage,
                correctAnswers: item.correctAnswers,
                incorrectAnswers: item.incorrectAnswers,
                updatedAt: updatedAt && !isNaN(updatedAt.getTime()) ? updatedAt : null,
              };
            })
            .sort((a: ResultHistory, b: ResultHistory) => a.attempt - b.attempt);
          console.log('Historique complet trié:', resultHistory.value);
        } else {
          throw new Error('Format de réponse invalide pour l\'historique');
        }
      } catch (err: any) {
        console.error('Erreur lors de la récupération de l\'historique:', err);
        error(err.response?.data?.message || 'Impossible de récupérer l\'historique des scores');
      }
    };

    const saveTestResults = async (attempt: number) => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible d\'enregistrer les résultats');
        error('Session non trouvée. Veuillez recommencer le test.');
        return false;
      }

      try {
        const formattedAnswers = Object.entries(answers.value).map(([questionId, answer]) => {
          const question = questions.value.find(q => q.id === Number(questionId));
          if (!question) {
            console.error(`Question non trouvée pour ID ${questionId}`);
            return null;
          }
          if (question.type === 'choix_multiple') {
            return {
              questionId: Number(questionId),
              propositionId: Number(answer),
              contenu: null,
            };
          } else if (question.type === 'oui_non') {
            const proposition = question.propositions?.find(p => p.contenu === answer);
            return {
              questionId: Number(questionId),
              propositionId: proposition ? proposition.id : null,
              contenu: answer,
            };
          } else {
            return {
              questionId: Number(questionId),
              propositionId: null,
              contenu: answer,
            };
          }
        }).filter(answer => answer !== null);

        const submissionData = {
          attempt,
          score: testResults.score,
          total: testResults.total,
          percentage: testResults.percentage,
          correctAnswers: testResults.correctAnswers,
          incorrectAnswers: testResults.incorrectAnswers,
          answers: formattedAnswers,
        };

        console.log('Envoi des résultats à l\'API:', submissionData);

        const response = await ApiService.post(`/sessions/${sessionId.value}/results`, submissionData);

        console.log('Réponse API enregistrement des résultats:', response);

        if (response.data && (response.data.code === 201 || response.data.code === 200)) {
          console.log('✓ Résultats enregistrés avec succès pour la tentative:', attempt);
          return true;
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'enregistrement des résultats');
        }
      } catch (err: any) {
        console.error('Erreur lors de l\'enregistrement des résultats:', err);
        let errorMessage = 'Erreur lors de l\'enregistrement des résultats. Veuillez réessayer.';
        if (err.response?.status === 404) {
          errorMessage = 'L\'endpoint pour enregistrer les résultats est introuvable. Vérifiez la configuration du serveur.';
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        }
        error(errorMessage);
        return false;
      }
    };

    const saveAnswer = async (questionId: number, answer: string, attempt: number) => {
      if (!sessionId.value) {
        console.error('Session ID manquant, impossible d\'enregistrer la réponse');
        return false;
      }
      const question = questions.value.find(q => q.id === questionId);
      if (!question) {
        console.error('Question non trouvée');
        return false;
      }

      console.log(`Enregistrement de la réponse pour la question ${questionId} (tentative ${attempt}):`, { type: question.type, answer });

      let formattedAnswer;
      if (question.type === 'choix_multiple') {
        formattedAnswer = {
          questionId: questionId,
          propositionId: Number(answer),
          contenu: null,
          attempt,
        };
      } else if (question.type === 'oui_non') {
        const proposition = question.propositions?.find(p => p.contenu === answer);
        formattedAnswer = {
          questionId: questionId,
          propositionId: proposition ? proposition.id : null,
          contenu: answer,
          attempt,
        };
      } else {
        formattedAnswer = {
          questionId: questionId,
          propositionId: null,
          contenu: answer,
          attempt,
        };
      }

      try {
        const submissionData = {
          sessionId: sessionId.value,
          answers: [formattedAnswer],
        };

        console.log('Envoi de la réponse à l\'API:', submissionData);

        const response = await ApiService.post('/reponses', submissionData);

        console.log('Réponse API enregistrement:', response);

        if (response.data && (response.data.code === 201 || response.data.code === 200)) {
          console.log(`✓ Réponse enregistrée avec succès pour la question ${questionId} (tentative ${attempt})`);
          return true;
        } else {
          throw new Error(response.data?.message || 'Erreur lors de l\'enregistrement');
        }
      } catch (err: any) {
        console.error('Erreur lors de l\'enregistrement de la réponse:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Erreur inconnue';
        error(`Erreur lors de l'enregistrement: ${errorMessage}`);
        return false;
      }
    };

    const onAnswerChange = () => {
      console.log('Réponse sélectionnée:', formData.answer);
      if (testForm.value) {
        testForm.value.setFieldValue('answer', formData.answer);
        testForm.value.validateField('answer');
      }
    };

    const previousQuestion = () => {
      if (currentQuestionIndex.value > 0) {
        if (currentQuestion.value && formData.answer) {
          answers.value[currentQuestion.value.id] = formData.answer;
        }
        currentQuestionIndex.value--;
        const savedAnswer = answers.value[currentQuestion.value?.id || 0];
        formData.answer = savedAnswer || null;
        if (testForm.value) {
          testForm.value.resetForm({ values: { answer: formData.answer } });
        }
        onAnswerChange();
      }
    };

    const handleSubmit = async (values: FormValues) => {
      console.log('=== SOUMISSION ===');
      console.log('Valeurs soumises:', values);
      console.log('Question actuelle:', currentQuestion.value);
      
      if (!currentQuestion.value) {
        error('Aucune question disponible.');
        return;
      }

      try {
        isSubmitting.value = true;
        await fetchResultHistory();
        const currentAttempt = resultHistory.value.length > 0 
          ? Math.max(...resultHistory.value.map(r => r.attempt)) + 1 
          : 1;

        const saveSuccess = await saveAnswer(currentQuestion.value.id, values.answer || '', currentAttempt);
        if (!saveSuccess) {
          error('Impossible d\'enregistrer votre réponse. Veuillez réessayer.');
          return;
        }

        answers.value[currentQuestion.value.id] = values.answer || '';
        console.log('Réponses sauvegardées:', answers.value);

        if (isLastQuestion.value) {
          let correctAnswers = 0;
          questions.value.forEach((question) => {
            const userAnswer = answers.value[question.id];
            if (userAnswer) {
              const correctProposition = question.propositions?.find(p => p.estLaBonneReponse);
              if (correctProposition) {
                if (question.type === 'choix_multiple' || question.type === 'oui_non') {
                  if (String(correctProposition.id) === userAnswer) {
                    correctAnswers++;
                  }
                } else if (question.type === 'texte_libre') {
                  if (userAnswer.toLowerCase().trim() === correctProposition.contenu.toLowerCase().trim()) {
                    correctAnswers++;
                  }
                }
              }
            }
          });

          Object.assign(testResults, {
            score: correctAnswers,
            total: questions.value.length,
            percentage: Math.round((correctAnswers / questions.value.length) * 100),
            correctAnswers: correctAnswers,
            incorrectAnswers: questions.value.length - correctAnswers,
          });

          const resultsSaved = await saveTestResults(currentAttempt);
          if (!resultsSaved) {
            error('Impossible d\'enregistrer les résultats. Veuillez réessayer.');
            return;
          }

          await new Promise(resolve => setTimeout(resolve, 500));

          await fetchTestResults();
          await fetchResultHistory();
          
          success('Test terminé avec succès ! Toutes vos réponses et résultats ont été enregistrés.');
          
          showResults.value = true;
        } else {
          currentQuestionIndex.value++;
          const nextQuestion = currentQuestion.value;
          const savedAnswer = answers.value[nextQuestion?.id || 0];
          formData.answer = savedAnswer || null;
          if (testForm.value) {
            testForm.value.resetForm({ values: { answer: formData.answer } });
          }
          onAnswerChange();
        }
      } catch (err: any) {
        console.error('Erreur lors de la soumission:', err);
        error(err.response?.data?.message || 'Une erreur est survenue.');
      } finally {
        isSubmitting.value = false;
      }
    };

    watch(
      () => route.params.id,
      () => {
        loadSessionQuestions();
      }
    );

    onMounted(() => {
      console.log('=== DÉBUT CHARGEMENT DES QUESTIONS ===');
      console.log('Paramètres de route:', route.params);
      loadSessionQuestions();
    });

    return {
      testForm,
      questions,
      currentQuestionIndex,
      currentQuestion,
      formData,
      testSchema,
      propositionOptions,
      isLastQuestion,
      isDisable,
      isLoading,
      isSubmitting,
      progressPercentage,
      answeredCount,
      sessionId,
      showResults,
      testResults,
      resultHistory,
      completeHistory,
      bestScore,
      averagePercentage,
      questionsWithAnswers,
      previousQuestion,
      handleSubmit,
      onAnswerChange,
      format_date,
    };
  },
});
</script>

<style scoped>
.card {
  border-radius: 0;
  background-color: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.results-container {
  background-color: transparent !important;
}

.form-control,
.form-check-input {
  border-radius: 0;
  border-color: #ced4da;
}

.text-black {
  color: #212529;
}

.text-danger {
  color: #dc3545;
  font-size: 0.875rem;
  font-weight: 500;
}

.text-success {
  color: #198754;
  font-weight: 500;
}

.border-primary {
  border-color: #0d6efd !important;
  border-width: 2px;
}

.btn-success {
  background-color: #198754;
  border-color: #198754;
}

.btn-info {
  background-color: #0dcaf0;
  border-color: #0dcaf0;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
}

.btn-primary {
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn:hover {
  filter: brightness(90%);
  transition: all 0.2s ease;
}

.cursor-not-allowed {
  cursor: not-allowed;
  opacity: 0.7;
}

.p-5 {
  padding: 5px;
}

.p-10 {
  padding: 10px;
}

.p-15 {
  padding: 15px;
}

.mb-5 {
  margin-bottom: 5px;
}

.mb-10 {
  margin-bottom: 10px;
}

.mb-15 {
  margin-bottom: 15px;
}

.mb-20 {
  margin-bottom: 20px;
}

.mt-10 {
  margin-top: 10px;
}

.mt-15 {
  margin-top: 15px;
}

.mt-20 {
  margin-top: 20px;
}

.fs-4 {
  font-size: 1.5rem;
  font-weight: 600;
}

.progress {
  background-color: #e9ecef;
  border-radius: 0;
}

.progress-bar {
  background-color: #198754;
  transition: width 0.3s ease;
  font-weight: 500;
}

.progress-bar.bg-danger {
  background-color: #dc3545 !important;
}

.alert {
  border-radius: 0;
  border: 1px solid;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
  color: #155724;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.spinner-border {
  width: 2rem;
  height: 2rem;
  border-color: #0d6efd;
  border-right-color: transparent;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.table-responsive {
  margin-bottom: 20px;
}

.table-bordered {
  border: 1px solid #dee2e6;
}

.table-bordered th,
.table-bordered td {
  border: 1px solid #dee2e6;
}

.table-light {
  background-color: #f8f9fa;
}

.table-primary {
  background-color: #cfe2ff !important;
}

.badge {
  padding: 0.35em 0.65em;
  font-size: 0.85em;
  font-weight: 500;
}

.questions-review {
  max-height: 600px;
  overflow-y: auto;
  padding-right: 10px;
}

.question-review-item {
  transition: all 0.3s ease;
  border-width: 2px;
  border-radius: 4px;
}

.question-review-item.border-success {
  border-color: #198754;
}

.question-review-item.border-danger {
  border-color: #dc3545;
}

.question-review-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.question-header {
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
}

.question-number {
  font-size: 1rem;
}

.bg-success-subtle {
  background-color: #e6f4ea !important;
}

.bg-danger-subtle {
  background-color: #f8d7da !important;
}

.text-decoration-line-through {
  text-decoration: line-through;
  text-decoration-color: #dc3545;
  text-decoration-thickness: 2px;
  color: #dc3545 !important;
  font-weight: 600;
}

.user-answer {
  border-left: 4px solid #6c757d;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.user-answer .text-success {
  color: #198754 !important;
}

.correct-answer-display {
  border-left: 4px solid #198754;
  background-color: #e6f4ea;
  border-radius: 4px;
}

.badge.bg-success {
  background-color: #198754 !important;
  color: #ffffff;
}

.badge.bg-danger {
  background-color: #dc3545 !important;
  color: #ffffff;
}

.badge.bg-secondary {
  background-color: #6c757d !important;
  color: #ffffff;
}

.badge.bg-primary {
  background-color: #0d6efd !important;
  color: #ffffff;
}
</style>